import { useEffect, useRef } from 'react';
import { useNotificationStore } from '../../store/notifications';
import type { Notification, NotificationCategory } from '../../store/notifications';

// ── Helpers pour grouper par date ──

function groupByDate(notifications: Notification[]): Record<string, Notification[]> {
  const groups: Record<string, Notification[]> = {};
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekStart = new Date(today.getTime() - today.getDay() * 86400000);

  for (const notif of notifications) {
    const d = new Date(notif.timestamp);
    let label: string;

    if (d >= today) {
      label = "Aujourd'hui";
    } else if (d >= yesterday) {
      label = 'Hier';
    } else if (d >= weekStart) {
      label = 'Cette semaine';
    } else {
      label = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
    }

    if (!groups[label]) groups[label] = [];
    groups[label].push(notif);
  }

  return groups;
}

const categoryLabels: Record<NotificationCategory | 'all', string> = {
  all: 'Toutes',
  alerts: 'Alertes',
  system: 'Systeme',
  investigations: 'Investigations',
  reports: 'Rapports',
};

const typeIcons: Record<string, JSX.Element> = {
  success: (
    <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  error: (
    <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
  warning: (
    <svg className="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  ),
  info: (
    <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  ),
  alert: (
    <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  ),
};

export default function NotificationPanel() {
  const panelOpen = useNotificationStore((s) => s.panelOpen);
  const setPanelOpen = useNotificationStore((s) => s.setPanelOpen);
  const notifications = useNotificationStore((s) => s.notifications);
  const filterCategory = useNotificationStore((s) => s.filterCategory);
  const setFilterCategory = useNotificationStore((s) => s.setFilterCategory);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  const panelRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!panelOpen) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    }
    // Delay to avoid closing immediately from the bell click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [panelOpen, setPanelOpen]);

  // Filtered notifications
  const filtered =
    filterCategory === 'all'
      ? notifications
      : notifications.filter((n) => n.category === filterCategory);

  const grouped = groupByDate(filtered);

  function handleNotificationClick(notif: Notification) {
    markAsRead(notif.id);
    if (notif.link) {
      window.location.href = notif.link;
      setPanelOpen(false);
    }
  }

  return (
    <>
      {/* Backdrop */}
      {panelOpen && (
        <div className="fixed inset-0 bg-black/30 z-[998] transition-opacity duration-300" />
      )}

      {/* Panel */}
      <div
        ref={panelRef}
        className={`
          fixed top-0 right-0 h-full w-96 max-w-[90vw] z-[999]
          bg-surface-secondary border-l border-border-subtle shadow-2xl
          transform transition-transform duration-300 ease-out
          ${panelOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-100">Notifications</h2>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-cyber-red/20 text-red-400">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Tout marquer lu
              </button>
            )}
            <button
              onClick={() => setPanelOpen(false)}
              className="p-1 rounded hover:bg-surface-tertiary text-gray-500 hover:text-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-1 px-5 py-3 border-b border-border-subtle overflow-x-auto">
          {(Object.keys(categoryLabels) as Array<NotificationCategory | 'all'>).map((cat) => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`
                px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors
                ${
                  filterCategory === cat
                    ? 'bg-cyber-cyan/20 text-cyan-300'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-surface-tertiary'
                }
              `}
            >
              {categoryLabels[cat]}
            </button>
          ))}
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100% - 120px)' }}>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <svg
                className="w-16 h-16 text-gray-700 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                />
              </svg>
              <p className="text-sm text-gray-500">Aucune notification</p>
              <p className="text-xs text-gray-600 mt-1">
                Les nouvelles notifications apparaitront ici
              </p>
            </div>
          ) : (
            Object.entries(grouped).map(([dateLabel, notifs]) => (
              <div key={dateLabel}>
                <div className="sticky top-0 px-5 py-2 bg-surface-secondary/95 backdrop-blur-sm">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    {dateLabel}
                  </span>
                </div>
                {notifs.map((notif) => (
                  <button
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`
                      w-full text-left px-5 py-3 border-b border-border-subtle/50
                      hover:bg-surface-tertiary/50 transition-colors
                      ${notif.read ? 'opacity-60' : ''}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {typeIcons[notif.type] || typeIcons.info}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {!notif.read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                          )}
                          <p className="text-sm font-medium text-gray-200 truncate">
                            {notif.title}
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                        <span className="text-[10px] text-gray-600 mt-1 inline-block">
                          {new Date(notif.timestamp).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      {notif.link && (
                        <svg
                          className="w-4 h-4 text-gray-600 flex-shrink-0 mt-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
